import { Grapheme, Morpheme, Word } from '@/common/types/scopeAndSequence';
import { LevelProgress } from '@/common/types/student';
import { playWordAudio, soundOutWord } from '@/common/utils/audio';
import confusableMap from '@/common/utils/confusableMap';
import { HeartWordsCard } from '@/game/GameComponents/PhaserGame/Types/IMinigameCardData';
import { v4 as uuidv4 } from 'uuid';
import scopeAndSequence from '../data/scopeAndSequence.json';
import {
  getCumulativeGraphemes,
  getGraphemesByIds,
  getGraphemesBySpelling,
  getWordGraphemes,
} from './scopeAndSequence';

export const GAMES = [
  'mysticMatch',
  'wordWeaver',
  'wordSums',
  'phonemeForge',
  'morphMaster',
  'sentenceBuilder',
] as const;

export const sanitize = (str: string): string => {
  return str.toLowerCase().replace(/[.,/#!$%^&*;:{}=\-_`~()?[\]"'<>]/g, '');
};

export function chunk<T>(arr: T[], size: number): T[][] {
  return Array.from({ length: Math.ceil(arr.length / size) }, (_, i) =>
    arr.slice(i * size, i * size + size),
  );
}

export function dealHeartWordsCards(
  correctHeartWordsCard: HeartWordsCard | undefined,
  trickyWordsArray: Word[],
): HeartWordsCard[] {
  return [
    ...trickyWordsArray
      .filter((word) => word.spelling !== correctHeartWordsCard?.word.spelling)
      .map((word) => {
        return {
          id: uuidv4(),
          word: word,
          isCorrect: false,
        };
      })
      .sort(() => Math.random() - 0.5)
      .slice(0, 2),
  ]
    .concat(correctHeartWordsCard ? [correctHeartWordsCard] : [])
    .sort(() => Math.random() - 0.5);
}

export function selectCorrectItem<T extends { id: string }>(
  itemsForRound: T[],
  correctItemCounts: Record<string, number>,
  previousItem: T | undefined,
): T | undefined {
  let unmasteredItems = itemsForRound.filter(
    (i) => !correctItemCounts[i.id] || correctItemCounts[i.id] < 2,
  );

  if (previousItem && unmasteredItems.length > 1) {
    unmasteredItems = unmasteredItems.filter((i) => i.id !== previousItem.id);
  }

  if (unmasteredItems.length === 0) {
    console.error('No unmastered items available.');
    return;
  }

  return unmasteredItems[Math.floor(Math.random() * unmasteredItems.length)];
}

export function getDistinctGraphemes(
  required: Grapheme[],
  options: Grapheme[],
  filterPronunciations: boolean = false,
): Grapheme[] {
  const seenSpellings = new Set(required.map((g) => g.spelling));
  const seenPronunciations = new Set(required.map((g) => g.pronunciation));
  const distinct: Grapheme[] = [];

  for (const opt of options) {
    if (seenSpellings.has(opt.spelling)) continue;

    if (filterPronunciations) {
      if (seenPronunciations.has(opt.pronunciation)) continue;

      // get all pronunciations for this spelling
      const allPronunciations = getGraphemesBySpelling(opt.spelling);
      // if any of them already seen, skip
      if (
        allPronunciations.some((p) => seenPronunciations.has(p.pronunciation))
      ) {
        continue;
      }
    }

    distinct.push(opt);
    seenSpellings.add(opt.spelling);
    seenPronunciations.add(opt.pronunciation);
  }

  return distinct;
}

export function getDistinctMorphemes(
  required: Morpheme[],
  options: Morpheme[],
): Morpheme[] {
  const seenSpellings = new Set(required.map((m) => m.spelling));
  const distinct: Morpheme[] = [];

  for (const opt of options) {
    if (seenSpellings.has(opt.spelling)) continue;

    distinct.push(opt);
    seenSpellings.add(opt.spelling);
  }

  return distinct;
}

export function mapActivityToSkill(activityName: string) {
  const activitySkillMap: {
    [key: string]: { CleanName: string; Skill: string };
  } = {
    wordWeaver: { CleanName: 'Word Weaver', Skill: 'Word building' },
    phonemeForge: { CleanName: 'Phoneme Forge', Skill: 'Sound manipulation' },
    mysticMatch: { CleanName: 'Mystic Match', Skill: 'Letter sound matching' },
    sentenceBuilder: {
      CleanName: 'Sentence Builder',
      Skill: 'Sentence construction',
    },
    trickyWords: { CleanName: 'Tricky Words', Skill: 'High frequency words' },
    wordRifts: { CleanName: 'Word Rifts', Skill: 'Comprehension' },
    wordSums: { CleanName: 'Word Sums', Skill: 'Morphology' },
    morphMaster: { CleanName: 'Morph Master', Skill: 'Morpheme manipulation' },
    assessment: { CleanName: 'Assessment Result', Skill: '' },
  };

  return activitySkillMap[activityName] || null;
}

export function resetLevelProgress(
  level: number,
  levelsProgress: LevelProgress[],
) {
  const levelIdx = level - 1;
  if (levelIdx < 0 || levelIdx > levelsProgress.length - 1) {
    console.error('Cannot reset level progress: invalid level');
    return levelsProgress;
  }
  console.log('Resetting level progress for level', level);
  const { levelId, completedPlaythroughs, gamesProgress } =
    levelsProgress[levelIdx];
  const updatedLevelProgress: LevelProgress = {
    levelId,
    completedPlaythroughs,
    gamesProgress: {},
  };
  Object.keys(gamesProgress).forEach((game) => {
    updatedLevelProgress.gamesProgress[game] = {
      ...gamesProgress[game],
      timesMastered: completedPlaythroughs,
      timesPlayed: completedPlaythroughs,
      currentHighestMastery: 0,
    };
  });
  return [
    ...levelsProgress.slice(0, levelIdx),
    updatedLevelProgress,
    ...levelsProgress.slice(levelIdx + 1),
  ];
}

export async function soundOutScopeAndSequenceWord(spelling: string) {
  // Try to find the word in S&S
  const word = scopeAndSequence.words.find(
    (wordObj) => wordObj.spelling === spelling,
  ) as Word | undefined;

  // Sound out the spelling
  await playWordAudio(spelling);

  // If word was found, sound out the graphemes
  if (word) {
    const graphemes = getWordGraphemes(word);
    await soundOutWord(graphemes, 0);
    return;
  } else {
    console.log(`Word "${spelling}" not found in scope and sequence data.`);
  }
}

/**
 * Select a weighted subset of items
 * @param items Array of items to choose from
 * @param weight Returns the probability ratio that the item is chosen
 * @param n Number of items to select
 */
export function weightedSample<T>(
  items: T[],
  weight: (item: T) => number,
  n: number,
): T[] {
  if (n <= 0 || !items.length) return [];

  const result: T[] = [];

  // make a copy so we can remove items as we go
  const pool = items.slice();

  // select until we have n items or run out
  for (let k = 0; k < n && pool.length > 0; k++) {
    const weights = pool.map((i) => Math.max(0, weight(i)));
    const total = weights.reduce((a, b) => a + b, 0);
    if (total === 0) break;

    // pick a random number as our threshold
    let r = Math.random() * total;
    let chosen = 0;
    for (let i = 0; i < weights.length; i++) {
      // subtract the next weight off the threshold until we reach 0
      // bigger weight = bigger subtraction = more likely to reach 0 at that item
      r -= weights[i];
      if (r <= 0) {
        // pick the item that triggered the threshold
        chosen = i;
        break;
      }
    }

    // remove the item so we don't pick it again
    const item = pool.splice(chosen, 1)[0];
    result.push(item);
  }

  return result;
}

const SAME_SOUND_DISTRACTOR_WEIGHT = 10;
const CONFUSABLE_SOUND_DISTRACTOR_WEIGHT = 8;
export function getDistractorGraphemeWeight(
  g: Grapheme,
  wordGraphemes: Grapheme[],
): number {
  // Tier 1: same sound, different spelling (already guaranteed different spelling by getDistinctGraphemes)
  if (wordGraphemes.some((wg) => wg.pronunciation === g.pronunciation))
    return SAME_SOUND_DISTRACTOR_WEIGHT;

  // Tier 2: confusable-but-distinct sound
  if (
    wordGraphemes.some((wg) =>
      confusableMap.get(wg.pronunciation)?.has(g.pronunciation),
    )
  )
    return CONFUSABLE_SOUND_DISTRACTOR_WEIGHT;

  return 1;
}

export function getDistractorGraphemes(
  wordGraphemes: Grapheme[],
  level: number,
  count: number,
  filterPronunciations: boolean = false,
): Grapheme[] {
  const cumulativeGraphemes = getGraphemesByIds(
    getCumulativeGraphemes(level),
  ).filter((g) => !g.spelling.includes('_'));
  const distinctGraphemes = getDistinctGraphemes(
    wordGraphemes,
    cumulativeGraphemes,
    filterPronunciations,
  );
  return weightedSample(
    distinctGraphemes,
    (g) => getDistractorGraphemeWeight(g, wordGraphemes),
    count,
  );
}
